import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Pricing", href: "/pricing" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export function PublicNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          aria-label="Financially Fit home"
        >
          <img
            src="/pwa-192x192.png"
            alt="Financially Fit logo"
            width={36}
            height={36}
            className="w-9 h-9 rounded-lg"
          />
          <span className="font-bold text-lg text-foreground hidden sm:block">Financially Fit</span>
        </Link>

        {/* Desktop links */}
        <ul className="hidden md:flex items-center gap-6" role="list">
          {navLinks.map((link) => (
            <li key={link.href}>
              <Link
                to={link.href}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary",
                  location.pathname === link.href ? "text-primary" : "text-muted-foreground"
                )}
                aria-current={location.pathname === link.href ? "page" : undefined}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <Button
              size="sm"
              asChild
              data-gtm-event="nav_dashboard_click"
            >
              <Link to="/dashboard" aria-label="Go to your Financially Fit dashboard" className="flex items-center gap-1.5">
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Link>
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                asChild
                data-gtm-event="nav_signin_click"
              >
                <Link to="/auth" aria-label="Sign in to Financially Fit">
                  Sign In
                </Link>
              </Button>
              <Button
                size="sm"
                asChild
                data-gtm-event="nav_getstarted_click"
              >
                <Link to="/auth" aria-label="Get started with Financially Fit for free">
                  Get Started Free
                </Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          id="mobile-menu"
          className="md:hidden border-t border-border/50 bg-background px-4 py-4 space-y-3 animate-fade-in"
        >
          <ul role="list" className="space-y-2">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "block py-2 text-sm font-medium transition-colors hover:text-primary",
                    location.pathname === link.href ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-2 pt-2 border-t border-border/50">
            {user ? (
              <Button asChild data-gtm-event="mobile_nav_dashboard_click" className="flex items-center gap-2">
                <Link to="/dashboard" onClick={() => setOpen(false)}>
                  <LayoutDashboard className="h-4 w-4" />
                  Go to Dashboard
                </Link>
              </Button>
            ) : (
              <>
                <Button variant="outline" asChild data-gtm-event="mobile_nav_signin_click">
                  <Link to="/auth" onClick={() => setOpen(false)}>
                    Sign In
                  </Link>
                </Button>
                <Button asChild data-gtm-event="mobile_nav_getstarted_click">
                  <Link to="/auth" onClick={() => setOpen(false)}>
                    Get Started Free
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}